import { TextField } from "@mui/material";
import dayjs from "dayjs";
import {
  type DateRange,
  type DayEventHandler,
  type DayPickerProps,
  type Matcher,
} from "react-day-picker";

import { CalendarBase } from "./CalendarBase";

type FieldInfo = {
  path: string;
  value: Date | undefined;
  label: string;
};

export type CalendarProps = {
  startField: FieldInfo;
  endField?: FieldInfo;
  setFieldValue: (path: string, value: Date | undefined) => void;
  dropdown?: boolean;
  disabledDates?: Matcher;
  startMonth?: Date;
  endMonth?: Date;
  numberOfMonths?: number;
  allowSameDayRangeSelection?: boolean;
};

export function Calendar({
  startField,
  endField,
  setFieldValue,
  dropdown,
  disabledDates,
  startMonth,
  endMonth,
  numberOfMonths,
  allowSameDayRangeSelection,
}: CalendarProps) {
  const selectedDay: Date | undefined = startField.value;

  const selectedRange: DateRange = {
    from: startField.value,
    to: endField?.value,
  };

  const handleDayClick: DayEventHandler<React.MouseEvent> = (date) => {
    // single date selection
    if (!endField) {
      setFieldValue(startField.path, date);
      return;
    }

    // date range selection
    if (selectedRange.to) {
      // whole range was already selected, begin to select new range
      setFieldValue(startField.path, date);
      setFieldValue(endField.path, undefined);
      return;
    }

    // make sure dates in range are in proper order
    if (dayjs(date).isBefore(selectedRange.from)) {
      setFieldValue(startField.path, date);
      setFieldValue(endField.path, selectedRange.from);
      return;
    }

    // same from and to dates selected
    if (!allowSameDayRangeSelection && dayjs(date).isSame(selectedRange.from)) {
      return;
    }

    setFieldValue(endField.path, date);
  };

  const fromValue = endField?.value ? selectedRange.from : selectedDay;

  const pickerProps = {
    captionLayout: dropdown ? "dropdown" : "label",
    disabled: disabledDates,
    defaultMonth: selectedDay || new Date(),
    required: true,
    startMonth,
    endMonth,
    numberOfMonths: numberOfMonths || 1,
    onSelect: () => {}, // needed to keep the calendar in controlled mode
    onDayClick: handleDayClick,
  } satisfies Partial<DayPickerProps>;

  return (
    <div
      className="flex min-h-0 shrink grow basis-auto flex-col items-center justify-start pt-2"
      data-sentry-block
    >
      <div className="mb-8 flex w-full gap-6">
        <TextField
          value={fromValue ? dayjs(fromValue).format("ddd, D MMM") : ""}
          className="pointer-events-none w-[50%]"
          label={startField.label}
          slotProps={{
            input: {
              readOnly: true,
            },
            inputLabel: {
              shrink: true,
            },
          }}
        />
        {endField ? (
          <TextField
            value={
              selectedRange.to
                ? dayjs(selectedRange.to).format("ddd, D MMM")
                : ""
            }
            className="pointer-events-none w-[50%]"
            label={endField.label}
            slotProps={{
              input: {
                readOnly: true,
              },
              inputLabel: {
                shrink: true,
              },
            }}
          />
        ) : (
          <div className="w-[50%]" />
        )}
      </div>

      {endField ? (
        <CalendarBase
          {...pickerProps}
          mode="range"
          styles={{
            today: {
              boxShadow: "0 0 0 1px #CFD4DD !important",
              color: "#222930",
            },
          }}
          selected={selectedRange}
        />
      ) : (
        <CalendarBase
          {...pickerProps}
          mode="single"
          styles={{
            selected: {
              backgroundColor: "#003E2B", // Replace with your desired color
              color: "#FFFFFF", // Optional: text color for selected days
            },
          }}
          selected={selectedDay}
        />
      )}
    </div>
  );
}
